// Required for `moment.tz.guess`
import i18n from './i18n';
import moment, { Moment, MomentInput } from 'moment-timezone';
import { captureException } from './sentry';
import {
  Timeslot,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';

type DateArgTypes = string | Date | Moment | null | undefined;
type DateFormatReturnType<T extends DateArgTypes> = T extends
  | string
  | Date
  | Moment
  ? string
  : null;

// Follows JS Date.getDay() ordering (0 = Sunday … 6 = Saturday).
// Yelp's 0 = Monday convention is converted to this in the loader.
export const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export const NUM_DAYS_OF_WEEK = 7;

export const TIME_FORMAT = 'HH:mm:ss';

const getTimeZoneFromMoment = (dateMoment: Moment) => {
  let timeZone = dateMoment.tz();
  if (!timeZone) {
    captureException(new Error('passed in date missing timezone'));
    timeZone = moment.tz.guess();
  }
  return timeZone;
};

/**
 * Returns the current date in the specified format, with an optionally given timezone.
 * Uses moment.js to handle timezone adjustments.
 *
 * @param {string} format - The format string for the date. Defaults to 'YYYY-MM-DD'.
 * @param {string} timezone - timezone (e.g. "America/New_York"). Defaults to the
 * device timezone.
 *
 * @returns {string} The current date in the specified format. Example: "2025-07-29"
 */
export const getTodayDate = (
  format = 'YYYY-MM-DD',
  timezone: string = moment.tz.guess(),
): string => {
  return moment().tz(timezone).format(format);
};

export const formatDate = <T extends DateArgTypes>(
  date: T,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  },
): DateFormatReturnType<T> => {
  if (date === null || date === undefined) {
    return <DateFormatReturnType<T>>null;
  }

  const dateMoment = moment(date);
  if (dateMoment.isValid()) {
    options.timeZone = getTimeZoneFromMoment(dateMoment);
    return <DateFormatReturnType<T>>localizeMomentDate(dateMoment, options);
  } else {
    captureException(new Error('Tried formatting malformed date string'));
    return <DateFormatReturnType<T>>'';
  }
};

/** Convert a MomentJS date into a localized string */
export function localizeMomentDate(
  date: moment.Moment,
  options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
  },
) {
  options.timeZone = getTimeZoneFromMoment(date);
  return date.toDate().toLocaleDateString(i18n.locale, options);
}

/** Convert a MomentJS time into a localized string */
export function localizeMomentTime(
  date: moment.Moment,
  options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  },
) {
  options.timeZone = getTimeZoneFromMoment(date);
  return date.toDate().toLocaleTimeString(i18n.locale, options);
}

// "5/23" for May 23rd
export const formatShortDate = <T extends DateArgTypes>(
  date: T,
): DateFormatReturnType<T> => {
  return formatDate(date, {
    day: 'numeric',
    month: 'numeric',
  });
};

// "May 23" for May 23rd
export const formatSummaryDate = <T extends DateArgTypes>(
  date: T,
): DateFormatReturnType<T> => {
  return formatDate(date, {
    day: 'numeric',
    month: 'short',
  });
};

// "May 23" for current-year dates, "May 23, 2022" for non-current-year dates
export const formatSummaryDateWithYear = <T extends DateArgTypes>(
  date: T,
): DateFormatReturnType<T> => {
  if (date === null || date === undefined) {
    return <DateFormatReturnType<T>>null;
  }

  const dateMoment = moment(date);
  const isCurrentYear = dateMoment.year() === moment().year();

  return formatDate(date, {
    day: 'numeric',
    month: 'short',
    ...(isCurrentYear ? {} : { year: 'numeric' }),
  });
};

// "Fri, May 23" for May 23rd
export const formatDateByWeekday = <T extends DateArgTypes>(
  date: T,
): DateFormatReturnType<T> => {
  return formatDate(date, {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });
};

// "Feb 25, 2025"
export const formatDateWithYear = <T extends DateArgTypes>(
  date: T,
): DateFormatReturnType<T> => {
  return formatDate(date, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatTime = (date: MomentInput) => {
  return moment(date).format('LT');
};

/**
 * Formats a given date into a `"2:30pm"`–style string.
 *
 * @param {MomentInput} date  The date value to format.
 * @returns {string} The formatted time string.
 */
export const formatCompactTime = (date: MomentInput) => {
  return moment(date).format('h:mma');
};

export const getWeekdayString = (dayOfWeek: number) => {
  return moment.weekdays(dayOfWeek);
};

export const militaryTimeToHourAndMinutes = (
  time: string,
): [number, number] => {
  const m = moment(time, 'kk:mm');
  return [m.get('hour'), m.get('minute')];
};

export const get12HourFormat = (hour: number, minute: number): string => {
  const timeMoment = moment().hour(hour).minute(minute);
  const timeFormat = moment.locale() === 'zh-cn' ? 'A h点mm分' : 'h:mm A';
  return timeMoment.format(timeFormat);
};

export const formatMonthDayInTimezone = (
  date: MomentInput,
  timezone: string,
): string => moment(date).tz(timezone).format('MMM D');

export const formatTimeslot = (
  timeslot: Timeslot,
  timezones: TimeZoneMap,
  includeTimezone = false,
) => {
  const startTimeMoment = moment.tz(
    timeslot.startTime,
    TIME_FORMAT,
    timezones.supplier,
  );
  const endTimeMoment = moment.tz(
    timeslot.endTime,
    TIME_FORMAT,
    timezones.supplier,
  );

  const isSameMeridiem =
    startTimeMoment.format('a') === endTimeMoment.format('a');

  const isChinese = moment.locale() === 'zh-cn';

  // Set timezone to restaurant time to do the conversion
  const startTimeInRestaurantTime = startTimeMoment
    .clone()
    .tz(timezones.restaurant);
  const endTimeInRestaurantTime = endTimeMoment
    .clone()
    .tz(timezones.restaurant);

  const baseTimeFormat = isChinese ? 'h点mm分' : 'h:mm';
  const timeFormatWithMeridiem = isChinese
    ? `a${baseTimeFormat}`
    : `${baseTimeFormat}a`;

  const timeFormatWithTimezone = includeTimezone
    ? `${timeFormatWithMeridiem} z`
    : timeFormatWithMeridiem;

  const firstTimeFormat =
    isChinese || !isSameMeridiem ? timeFormatWithMeridiem : baseTimeFormat;
  const lastTimeFormat = isChinese
    ? isSameMeridiem
      ? includeTimezone
        ? `${baseTimeFormat} z`
        : baseTimeFormat
      : timeFormatWithTimezone
    : timeFormatWithTimezone;
  return `${startTimeInRestaurantTime.format(
    firstTimeFormat,
  )}-${endTimeInRestaurantTime.format(lastTimeFormat)}`;
};

export const momentWithTimezone = (datetime?: string | null) => {
  // `moment.local` doesn't set the timezone on the moment object so use
  // `moment.tz.guess` instead
  return moment(datetime).tz(moment.tz.guess());
};

/**
 * Formats a date range for dates in the same month and year by combining the
 * start and end day parts of the date range
 * @param startDateMoment - Start date to format
 * @param endDateMoment - End date to format
 * @param timezone - Timezone to interpret the dates in
 * @returns Formatted date range
 */
const _formatCombinedDayDateRange = (
  startDateMoment: Moment,
  endDateMoment: Moment,
  nowMoment: Moment,
  timezone: string,
) => {
  const dateFormatter = new Intl.DateTimeFormat(i18n.locale, {
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
    year:
      // Show year if it's not the same as the current year
      startDateMoment.year() === nowMoment.year() ? undefined : 'numeric',
  });
  const startDateParts = dateFormatter.formatToParts(startDateMoment.toDate());
  const endDateParts = dateFormatter.formatToParts(endDateMoment.toDate());
  return startDateParts
    .map((part) => {
      if (part.type === 'day') {
        const endDay = endDateParts.find((endPart) => endPart.type === 'day');
        if (!endDay) {
          // Fallback to just the start day if end day part is missing
          return part.value;
        }

        return [part.value, endDay.value].join('–');
      }

      return part.value;
    })
    .join('');
};

/**
 * Formats a date range by month
 * @param startDateInput - Start date to format
 * @param endDateInput - End date to format
 * @param timezone - Timezone to interpret the dates in
 * @returns Formatted date range
 */
export const formatDateRangeByMonth = (
  startDateInput: DateArgTypes,
  endDateInput: DateArgTypes,
  timezone: string = moment.tz.guess(),
) => {
  const startDateMoment = moment.tz(startDateInput, timezone);
  if (!startDateMoment.isValid()) {
    return '--';
  }

  const nowMoment = moment();
  const endDateMoment = moment.tz(endDateInput, timezone);

  if (!endDateMoment.isValid()) {
    return i18n.t(startDateMoment > nowMoment ? 'starts' : 'started', {
      date: startDateMoment.toDate().toLocaleDateString(i18n.locale, {
        day: 'numeric',
        month: 'short',
        timeZone: timezone,
        // Show year if it's not the same as the current year
        year:
          startDateMoment.year() === nowMoment.year() ? undefined : 'numeric',
      }),
    });
  }

  // If the start and end dates are in the same month and year, combine the start and end days
  if (
    startDateMoment.month() === endDateMoment.month() &&
    startDateMoment.year() === endDateMoment.year()
  ) {
    return _formatCombinedDayDateRange(
      startDateMoment,
      endDateMoment,
      nowMoment,
      timezone,
    );
  }

  const dateParts = [
    startDateMoment.toDate().toLocaleDateString(i18n.locale, {
      day: 'numeric',
      month: 'long',
      timeZone: timezone,
      year:
        // Show year if the start date's year is not the same as the end date's year
        startDateMoment.year() === endDateMoment.year() ? undefined : 'numeric',
    }),
    endDateMoment.toDate().toLocaleDateString(i18n.locale, {
      day: 'numeric',
      month: 'long',
      timeZone: timezone,
      year:
        // Show year if it's not the same as both the start date's year and the current year
        startDateMoment.year() === endDateMoment.year() &&
        startDateMoment.year() === nowMoment.year()
          ? undefined
          : 'numeric',
    }),
  ];

  return dateParts.join('–');
};

/**
 * Force moment to refresh its timezone cache hourly to avoid edge cases with
 * timezone changes, while also avoiding slow timezone calculations every time
 * it's accessed
 */
let lastRefreshTime = 0;
const ONE_HOUR = 60 * 60 * 1000;
export const getDeviceTimezone = () => {
  const now = Date.now();

  if (now - lastRefreshTime > ONE_HOUR) {
    lastRefreshTime = now;
    return moment.tz.guess(true);
  }

  return moment.tz.guess();
};

/**
 * Adjusts a UTC date string to a specific timezone
 * @param dateString - UTC date string
 * @param timeZone - Target timezone (usually restaurant timezone)
 * @returns Moment object adjusted for the specified timezone
 */
export const adjustMomentForTimezone = (
  dateString: string,
  timeZone: string,
): Moment => {
  const utcMoment = moment.utc(dateString);
  const year = utcMoment.year();
  const month = utcMoment.month();
  const day = utcMoment.date();

  return moment.tz({ day, month, year }, timeZone);
};

/**
 * Convert a date into a string with format "Saturday, June 5"
 * @param date - Date to format, can be a string, Date object, Moment object, or null/undefined
 * @returns Formatted date string or null if the input is null/undefined
 */
export const formatDateWithDayAndMonth = (
  date: DateArgTypes,
): DateFormatReturnType<DateArgTypes> => {
  if (date === null || date === undefined) {
    return <DateFormatReturnType<DateArgTypes>>null;
  }

  const dateMoment = moment(date);
  if (dateMoment.isValid()) {
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    };
    options.timeZone = getTimeZoneFromMoment(dateMoment);
    const formattedDate = dateMoment
      .toDate()
      .toLocaleDateString(i18n.locale, options);
    return <DateFormatReturnType<DateArgTypes>>(
      (formattedDate[0].toUpperCase() + formattedDate.slice(1))
    );
  } else {
    captureException(new Error('Tried formatting malformed date string'));
    return <DateFormatReturnType<DateArgTypes>>'';
  }
};

/**
 * Compares two ISO date strings for sorting.
 * @param a - First ISO date string
 * @param b - Second ISO date string
 * @returns Integer result for sorting: negative if a < b, positive if a > b, zero if equal
 */
export const compareISODates = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  const aKey = a ?? '';
  const bKey = b ?? '';
  return aKey.localeCompare(bKey);
};

export const momentToCalendarDate = (date: Moment): Date =>
  new Date(date.year(), date.month(), date.date());

export const calendarDateToMoment = (date: Date, timezone: string): Moment =>
  moment
    .tz(
      { day: date.getDate(), month: date.getMonth(), year: date.getFullYear() },
      timezone,
    )
    .startOf('day');

export const truncateTime = (time: Date, minuteInterval = 1): Date => {
  const truncated = new Date(time);
  const truncMinutes =
    Math.ceil(truncated.getMinutes() / minuteInterval) * minuteInterval;
  truncated.setMinutes(truncMinutes, 0, 0);

  return truncated;
};

export const getFulfillmentDateInRestaurantTimezone = (
  fulfillmentDateUtc: string,
  restaurantTimezone: string,
): Moment => {
  const selectedDateMoment = moment
    .utc(fulfillmentDateUtc)
    .tz(restaurantTimezone);

  const year = selectedDateMoment.year();
  const month = selectedDateMoment.month();
  const day = selectedDateMoment.date();

  return moment.utc({ day, month, year }).startOf('day');
};

/**
 * Calculates the difference in milliseconds between now and a target ISO date string.
 * @param validUntil - The expiration ISO string.
 */
export const getMillisecondsUntilExpiration = (validUntil: string): number => {
  const now = moment();
  const expiration = moment(validUntil);
  return Math.max(0, expiration.diff(now));
};

export const toHoursAndMinutes = (
  durationMs: number,
): { hours: number; minutes: number } => {
  const duration = moment.duration(durationMs);

  return {
    hours: Math.floor(duration.asHours()),
    minutes: duration.minutes(),
  };
};

export const toDaysAndHours = (
  durationMs: number,
): { days: number; hours: number } => {
  const duration = moment.duration(durationMs);

  return {
    days: Math.floor(duration.asDays()),
    hours: duration.hours(),
  };
};

/**
 * Formats a date with month, day, time, and conditionally year.
 * Includes year only for dates outside the current year.
 *
 * @param date - The date to format. If null/undefined, returns the current date/time.
 * @param timezone - Optional timezone to interpret the date in. Defaults to device timezone.
 *
 * @returns Formatted date string (e.g., "May 23, 1:02am" or "May 23 2022, 1:02am")
 */
export const formatDateTimeWithYear = <T extends DateArgTypes>(
  date?: T,
  timezone?: string,
): string => {
  const currentYearFormat = 'MMM D, h:mma';

  if (date === null || date === undefined) {
    return moment.tz(moment.tz.guess()).format(currentYearFormat);
  }

  const dateMoment = timezone
    ? moment.tz(date, timezone)
    : moment.tz(date, moment.tz.guess());
  const isCurrentYear = dateMoment.year() === moment().year();

  if (isCurrentYear) {
    return dateMoment.format(currentYearFormat);
  }

  return dateMoment.format('MMM D Y, h:mma');
};
